import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../i18n/useLang";
import { getBasePath, localizedPath } from "../i18n/routes";

const SITE_ORIGIN = "https://quadrocode.com";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/quadrocode-social.png`;

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertAlternate(language, href) {
  let element = document.head.querySelector(`link[rel="alternate"][hreflang="${language}"]`);
  if (!element) {
    element = document.createElement("link");
    element.rel = "alternate";
    element.hreflang = language;
    document.head.appendChild(element);
  }
  element.href = href;
}

function upsertJsonLd(id, value) {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(value);
}

export default function Seo({ title, description, canonical, image = DEFAULT_IMAGE, noindex = false }) {
  const { t, lang } = useLang();
  const location = useLocation();

  useEffect(() => {
    const finalTitle = title || t?.seo?.title || "QuadroCode";
    const finalDescription = description || t?.seo?.description || "";
    const pathname = location.pathname || "/";
    const finalCanonical = canonical || `${SITE_ORIGIN}${pathname}`;

    document.title = finalTitle;
    upsertMeta('meta[name="description"]', { name: "description", content: finalDescription });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "QuadroCode" });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: lang });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: finalTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: finalDescription });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: finalCanonical });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: "QuadroCode software development company",
    });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: finalTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: finalDescription });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    upsertLink("canonical", finalCanonical);
    const basePath = getBasePath(pathname);
    upsertAlternate("en", `${SITE_ORIGIN}${localizedPath(basePath, "en")}`);
    upsertAlternate("ar", `${SITE_ORIGIN}${localizedPath(basePath, "ar")}`);
    upsertAlternate("tr", `${SITE_ORIGIN}${localizedPath(basePath, "tr")}`);
    upsertAlternate("x-default", `${SITE_ORIGIN}${localizedPath(basePath, "en")}`);

    upsertJsonLd("qc-identity-schema", {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_ORIGIN}/#organization`,
      name: "QuadroCode",
      url: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/quadrocode-social.png`,
      email: "info@quadrocode.com",
      description: finalDescription,
    });
    upsertJsonLd("qc-website-schema", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_ORIGIN}/#website`,
      url: SITE_ORIGIN,
      name: "QuadroCode",
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      inLanguage: lang,
    });

    const faqSchema = document.getElementById("qc-faq-schema");
    const faqItems = t?.home?.footer?.faq?.items;
    if (getBasePath(pathname) === "/" && faqItems?.length) {
      upsertJsonLd("qc-faq-schema", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      });
    } else if (faqSchema) {
      faqSchema.remove();
    }
  }, [title, description, canonical, image, noindex, location.pathname, t, lang]);

  return null;
}
